import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * A plain table in the SmartSpaza style.
 *
 * Wrapped in its own `overflow-x-auto` container so a wide table scrolls inside
 * itself rather than making the page scroll sideways — a horizontally scrolling
 * *page* is how a column of prices ends up permanently off-screen.
 *
 * **Scrolling is the fallback, not the plan.** On a phone, sliding a seven-column
 * table sideways to find the total is a bad way to read one. So columns can say
 * which sizes they are worth showing at — see `hideBelow` on `TH` and `TD` — and
 * a well-marked table narrows to the three or four columns that answer the
 * question, with the rest available by opening the row. Marking a column is
 * opt-in and per column, because only whoever built the screen knows which of
 * them somebody actually came for.
 */
export function Table({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("scroll-thin -mx-5 overflow-x-auto px-5", className)}>
      <table className="w-full min-w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  );
}

/**
 * The smallest width a column is worth showing at.
 *
 * Named for the size it *appears* at rather than the size it disappears at,
 * because "show this from `sm` up" is the decision somebody is actually making
 * and `hidden sm:table-cell` is the mechanics of it.
 */
export type ColumnVisibility = "sm" | "md" | "lg" | "xl";

const VISIBILITY: Record<ColumnVisibility, string> = {
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
};

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-line text-left">{children}</tr>
    </thead>
  );
}

export function TH({
  children,
  className,
  hideBelow,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Hide this column below the named breakpoint.
   *
   * **A `TD` must carry the same value as its `TH`.** They are separate elements
   * with no way to find each other, so a mismatch silently shifts every cell
   * after it one column to the left — which looks like corrupt data rather than
   * a layout bug. `test_tables_hide_columns_in_pairs` fails the build on one.
   */
  hideBelow?: ColumnVisibility;
}) {
  return (
    <th
      scope="col"
      className={cn(
        "whitespace-nowrap px-3 py-2.5 text-xs font-semibold text-ink-500",
        hideBelow && VISIBILITY[hideBelow],
        className,
      )}
    >
      {children}
    </th>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-line">{children}</tbody>;
}

export function TR({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  /**
   * Makes the whole row activate something — opening a detail page, usually.
   *
   * When present the row also becomes keyboard-reachable and responds to Enter
   * and Space. A `<tr onClick>` alone is a trap: it works with a mouse and is
   * invisible to anybody using a keyboard or a screen reader, and an
   * administration portal that cannot be operated from the keyboard is one
   * somebody will be slower in every day.
   *
   * It is also what makes a narrowed table usable: the columns dropped on a
   * phone are reachable by opening the row, so hiding one hides it from a
   * glance rather than from the merchant.
   */
  onClick?: () => void;
}) {
  if (!onClick) {
    return <tr className={cn("hover:bg-canvas/60", className)}>{children}</tr>;
  }

  return (
    <tr
      className={cn(
        "cursor-pointer hover:bg-canvas/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500",
        className,
      )}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {children}
    </tr>
  );
}

export function TD({
  children,
  className,
  hideBelow,
}: {
  children: ReactNode;
  className?: string;
  /** Must match the `hideBelow` on this column's `TH`. See there. */
  hideBelow?: ColumnVisibility;
}) {
  return (
    <td
      className={cn(
        "px-3 py-3 align-middle text-ink-900",
        hideBelow && VISIBILITY[hideBelow],
        className,
      )}
    >
      {children}
    </td>
  );
}
